import { getGroups } from '@/apis/groups';
import { isTemplatePlaceholder } from '@/types';
import { AutocompleteGenericEntity, DomainCode } from '@australiangreens/ag-internal-components';
import { RuleProperties } from '@react-awesome-query-builder/mui';
import { GroupIdsFilter } from '@/types/searchFilterTypes';
import { FilterMapper, ValidationContext } from './filterMapperTypes';

export const loadGroups = async ({
  token,
  domainCode,
  groupIds,
}: {
  token: string;
  domainCode: DomainCode;
  groupIds: number[];
}) => {
  const groups = await getGroups(token, groupIds, domainCode);
  return { groups, isMissing: groups.length !== groupIds.length };
};

export const GroupIdsMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const isNoGroupSelected = value[0].value.length === 0;
    return isNoGroupSelected;
  },
  toFilter({ value }: RuleProperties): GroupIdsFilter {
    if (!value) throw new Error('Missing value');
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');

    const nestedValue = value[0].value;
    let mappedValue;
    if (isTemplatePlaceholder(nestedValue)) {
      mappedValue = nestedValue;
    } else {
      const groups = nestedValue as AutocompleteGenericEntity[];

      mappedValue = groups.map(({ id }) => {
        if (typeof id !== 'number') throw new Error('Invalid value. Expect array of numbers');
        return id;
      });
    }

    return { name: 'groupIds', value: mappedValue } as GroupIdsFilter;
  },

  async fromFilter(filter: GroupIdsFilter, { token, domainCode }: ValidationContext) {
    if (isTemplatePlaceholder(filter.value)) {
      return {
        field: filter.name,
        operator: 'any in',
        value: [
          {
            value: filter.value,
            errors: [],
          },
        ],
      };
    }

    const { groups, isMissing } = await loadGroups({ token, groupIds: filter.value, domainCode });

    return {
      field: filter.name,
      operator: 'any in',
      value: [
        {
          value: groups,
          errors: isMissing ? [{ field: 'groups', message: 'Missing groups' }] : [],
        },
      ],
    };
  },
};
